import { useState, useEffect } from 'react';
import { Modal, Button, Alert, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { BankAccountService } from '../../services/api/bankAccounts';
import { useConfirmation } from '../../hooks/useConfirmation';
import ResponsiblesService from '../../services/api/responsibles';
import BankAccountModal from './BankAccountModal';

const BanksLayer = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [responsibles, setResponsibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { modalProps, confirmDelete, confirmAction } = useConfirmation();

  // Função para carregar dados
  const loadData = async () => {
    setLoading(true);
    try {
      const [accountsData, banksData, responsiblesData] = await Promise.all([
        BankAccountService.getUserBankAccounts(),
        BankAccountService.getBanks(),
        ResponsiblesService.getResponsibles()
      ]);
      
      // A API retorna { success: true, data: [...], total: X, message: "..." }
      setAccounts(accountsData.data || []);
      setBanks(banksData.data || []);
      
      // Filtrar apenas responsáveis aprovados
      const approvedResponsibles = (responsiblesData.data || []).filter(r => r.status === 'approved');
      setResponsibles(approvedResponsibles);
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      if (error.response?.status === 401) {
        setError('Erro de autenticação. Faça login novamente.');
      } else {
        setError('Erro ao carregar dados. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Carregar contas bancárias e lista de bancos
  useEffect(() => {
    loadData();
  }, []);

  const handleSubmit = async (formData) => {
    try {
      setSaving(true);
      setError('');

      if (editingAccount) {
        await BankAccountService.updateBankAccount(editingAccount.id, formData);
        setSuccess('Conta bancária atualizada com sucesso!');
      } else {
        await BankAccountService.createBankAccount(formData);
        setSuccess('Conta bancária enviada para aprovação! Aguarde a análise do administrador.');
      }

      setShowModal(false);
      setEditingAccount(null);
      loadData();
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
      setError(error.response?.data?.message || 'Erro ao salvar conta. Tente novamente.');
      throw error; // Propagar erro para o modal
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (accountId) => {
    try {
      await BankAccountService.deleteBankAccount(accountId);
      setSuccess('Conta bancária removida com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao deletar conta:', error);
      setError('Erro ao deletar conta. Tente novamente.');
    }
  };

  const handleSetDefault = async (accountId) => {
    try {
      await BankAccountService.setDefaultAccount(accountId);
      setSuccess('Conta definida como padrão!');
      loadData();
    } catch (error) {
      console.error('Erro ao definir conta padrão:', error);
      setError('Erro ao definir conta padrão. Tente novamente.');
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setShowModal(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100);
  };

  return (
    <>
      <div className="card bg-transparent border-0">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title mb-0 d-flex align-items-center">
            <Icon icon="mdi:bank" className="me-2" />
            Minhas Contas Bancárias
          </h4>
          <Button 
            variant="primary" 
            onClick={() => setShowModal(true)}
            disabled={responsibles.length === 0}
            title={responsibles.length === 0 ? "É necessário ter pelo menos um responsável aprovado para cadastrar contas bancárias" : ""}
          >
            <Icon icon="mdi:plus" className="me-2" />
            Cadastrar Conta
          </Button>
        </div>
        
        {/* Alerta: Sem responsável aprovado */}
        {!loading && responsibles.length === 0 && (
          <Alert variant="info" className="m-3 d-flex align-items-start">
            <Icon icon="mdi:account-alert" className="me-2 mt-1" style={{ fontSize: '24px' }} />
            <div>
              <strong>Cadastre um Responsável primeiro</strong>
              <p className="mb-2 mt-1">
                Para cadastrar uma conta bancária, você precisa ter pelo menos um <strong>Responsável aprovado</strong>.
              </p>
              <Button
                variant="primary"
                size="sm"
                href="#/responsibles"
              >
                <Icon icon="mdi:account-plus" className="me-1" />
                Cadastrar Responsável
              </Button>
            </div>
          </Alert>
        )}

        {/* Disclaimer de Segurança */}
        <div className="alert alert-warning m-3 icon-card justify-content-start" role="alert">
          <Icon icon="mdi:shield-alert" className="me-2" />
          <strong>Importante:</strong> Só o titular da conta poderá solicitar o saque.
          Certifique-se de que os dados cadastrados estão corretos.
        </div>

        <div className="card-body">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <div className="table-responsive">
            <table className="table colored-row-table text-sm">
              <thead>
                <tr>
                  <th>Banco</th>
                  <th>Tipo</th>
                  <th>Agência/Conta</th>
                  <th>Titular</th>
                  <th>PIX</th>
                  <th>Status</th>
                  <th>Padrão</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-secondary-light">
                      Nenhuma conta cadastrada.
                    </td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <Icon icon="mdi:bank" className="text-primary" />
                          <div>
                            <div className="fw-semibold">{account.bank_name}</div>
                            <small className="text-muted">Código: {account.bank_code}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg={account.account_type === 'corrente' ? 'primary' : 'success'}>
                          {account.account_type === 'corrente' ? 'Corrente' : 'Poupança'}
                        </Badge>
                      </td>
                      <td>
                        <div>
                          <div>Ag: {account.agency}</div>
                          <div>Conta: {account.account_number}{account.account_digit ? `-${account.account_digit}` : ''}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-semibold">{account.holder_name}</div>
                          <small className="text-muted">{account.holder_document}</small>
                        </div>
                      </td>
                      <td>
                        {account.pix_key ? (
                          <div>
                            <Badge bg="success" className="mb-1">PIX</Badge>
                            <div className="small">{account.pix_key}</div>
                            <small className="text-muted">{account.pix_type}</small>
                          </div>
                        ) : (
                          <span className="text-muted">Não configurado</span>
                        )}
                      </td>
                      <td>
                        {account.approval_status === 'approved' ? (
                          <Badge bg="success">
                            <Icon icon="mdi:check-circle" className="me-1" />
                            Aprovada
                          </Badge>
                        ) : account.approval_status === 'rejected' ? (
                          <Badge bg="danger">
                            <Icon icon="mdi:close-circle" className="me-1" />
                            Rejeitada
                          </Badge>
                        ) : (
                          <Badge bg="warning">
                            <Icon icon="mdi:clock" className="me-1" />
                            Pendente
                          </Badge>
                        )}
                      </td>
                      <td>
                        {account.is_default ? (
                          <Badge bg="success">Padrão</Badge>
                        ) : account.approval_status === 'approved' ? (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleSetDefault(account.id)}
                          >
                            Definir padrão
                          </Button>
                        ) : (
                          <Badge bg="secondary">-</Badge>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="btn-group" role="group">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(account)}
                            title="Editar"
                            disabled={account.approval_status === 'approved'}
                            className='icon-card'
                          >
                            <Icon icon="mdi:pencil" />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => confirmDelete(
                              'Remover conta bancária',
                              'Tem certeza que deseja remover esta conta? Esta ação não pode ser desfeita.',
                              () => handleDelete(account.id)
                            )}
                            title="Remover"
                            disabled={account.approval_status === 'approved'}
                            className='icon-card'
                          >
                            <Icon icon="mdi:delete" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Moderno de Cadastro/Edição */}
      <BankAccountModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditingAccount(null);
        }}
        editingAccount={editingAccount}
        banks={banks}
        responsibles={responsibles}
        onSubmit={handleSubmit}
        loading={saving}
      />

      {/* Modal de confirmação */}
      <Modal {...modalProps} />
    </>
  );
};

export default BanksLayer;